import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  apiErrorMessage,
  getTestBuilderOptions,
  TEST_DIFFICULTIES,
} from "../../api/assessmentTests";

const FIELD_SX = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

export const EMPTY_DISTRIBUTION = { items: [], difficultyCounts: {} };

const keyOf = (i) => `${i.subjectId}:${i.chapterId}:${i.topicId ?? ""}:${i.subTopicId ?? ""}`;

const isValidCount = (count) => count !== "" && Number.isInteger(Number(count)) && Number(count) >= 1;

const availableHint = (available) => {
  if (!available) return null;
  const parts = TEST_DIFFICULTIES.filter((d) => available.byDifficulty?.[d.value]).map(
    (d) => `${d.label} ${available.byDifficulty[d.value]}`,
  );
  return `${available.total} available${parts.length ? ` (${parts.join(" · ")})` : ""}`;
};

function PickRow({ label, available, picked, count, onToggle, onCount, disabled, toggleDisabled, indent = 0 }) {
  const invalid = picked && !isValidCount(count);
  const over = picked && !invalid && available && Number(count) > available.total;
  let helperText;
  if (invalid) helperText = "Whole number 1 or more";
  else if (over) helperText = `Only ${available.total} available`;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        pl: indent * 3,
        py: 0.25,
        opacity: toggleDisabled && !picked ? 0.5 : 1,
      }}
    >
      <Checkbox
        size="small"
        inputProps={{ "aria-label": `Select ${label}` }}
        checked={picked}
        disabled={disabled || (toggleDisabled && !picked)}
        onChange={onToggle}
      />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: "0.85rem", fontWeight: picked ? 600 : 400 }}>{label}</Typography>
        {available && (
          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary" }}>{availableHint(available)}</Typography>
        )}
      </Box>
      {picked && (
        <TextField
          size="small"
          type="number"
          label="Qs"
          value={count}
          disabled={disabled}
          error={invalid || over}
          helperText={helperText}
          onChange={(e) => onCount(e.target.value)}
          inputProps={{ min: 1, max: available?.total }}
          sx={{ width: 130, ...FIELD_SX }}
        />
      )}
    </Box>
  );
}

// Parent contract: when courseId / testTypeId / level change because the user picked something else, the parent
// must clear value.items in the same state update (so no commit has a new type with the old items).
// multiChapter: the test type may mix subjects and chapters, so topics / sub-topics are picked from any chapter
// (each chapter's topics are loaded when it is opened) instead of from one subject + chapter.
// singleTopic: only one topic may have picks (Practice tests are keyed to one topic); once a topic has a pick the
// other topics are locked until it is unticked.
function TestDistributionBuilder({
  courseId,
  testTypeId,
  level,
  multiChapter = false,
  singleTopic = false,
  minPyqPercentage = 0,
  examMonth = "",
  examYear = "",
  totalQuestions,
  value,
  onChange,
  disabled,
}) {
  const [options, setOptions] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [localSubjectId, setLocalSubjectId] = useState("");
  const [localChapterId, setLocalChapterId] = useState("");

  const topicLevel = level === "TOPIC" || level === "TOPIC_SUBTOPIC";
  const multiTopicMode = topicLevel && multiChapter;
  // Single-chapter mode: one subject + chapter dropdown pair scopes the topic list.
  const singleScope = topicLevel && !multiChapter;
  const items = value.items;
  const first = items[0];
  const [topicsByChapter, setTopicsByChapter] = useState({});
  const [loadingChapters, setLoadingChapters] = useState({});
  const [expanded, setExpanded] = useState({});
  // PYQ tests restricted to one exam month: the available counts only include that month's questions
  const periodKey = examMonth !== "" && examYear !== "" ? `${examMonth}-${examYear}` : "";
  const periodParams = periodKey ? { examMonth, examYear } : {};

  // The scope follows the picked items when there are any (so a replaced `value` is always honoured).
  const subjectId = singleScope ? (first ? first.subjectId : localSubjectId) : "";
  const chapterId = singleScope ? (first ? first.chapterId : localChapterId) : "";

  useEffect(() => {
    setLocalSubjectId("");
    setLocalChapterId("");
    setOptions(null);
    setTopicsByChapter({});
    setLoadingChapters({});
    setExpanded({});
  }, [courseId, testTypeId, level, multiChapter]);

  // Keep the local scope in step with the saved items, so unticking the last item keeps its subject/chapter.
  const firstSubjectId = first?.subjectId;
  const firstChapterId = first?.chapterId;
  useEffect(() => {
    if (singleScope && firstSubjectId && firstChapterId) {
      setLocalSubjectId(firstSubjectId);
      setLocalChapterId(firstChapterId);
    }
  }, [singleScope, firstSubjectId, firstChapterId]);

  useEffect(() => {
    if (!courseId || !testTypeId || !level) {
      setOptions(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError("");
    getTestBuilderOptions({
      courseId,
      testTypeId,
      subjectId: singleScope && subjectId && chapterId ? subjectId : undefined,
      chapterId: singleScope && subjectId && chapterId ? chapterId : undefined,
      ...periodParams,
    })
      .then((data) => {
        if (!cancelled) setOptions(data);
      })
      .catch((err) => {
        if (!cancelled) {
          setOptions(null);
          setError(apiErrorMessage(err, "Failed to load distribution options"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId, testTypeId, level, singleScope, subjectId, chapterId, periodKey]);

  const chapterKey = (sId, cId) => `${sId}:${cId}`;

  // Multi-chapter mode: fetch the topics / sub-topics of several chapters in one request, skipping the
  // chapters that are already loaded or loading. `pairs` are [subjectId, chapterId].
  const loadChapterTopics = (pairs, force = false) => {
    const wanted = pairs.filter(([sId, cId]) => {
      const key = chapterKey(sId, cId);
      return force || (!topicsByChapter[key] && !loadingChapters[key]);
    });
    if (!wanted.length) return;
    setLoadingChapters((prev) => ({ ...prev, ...Object.fromEntries(wanted.map(([sId, cId]) => [chapterKey(sId, cId), true])) }));
    // subjectId / chapterId each take several ids; every linked subject + chapter combination comes back
    getTestBuilderOptions({
      courseId,
      testTypeId,
      subjectId: [...new Set(wanted.map(([sId]) => sId))].join(","),
      chapterId: [...new Set(wanted.map(([, cId]) => cId))].join(","),
      ...periodParams,
    })
      .then((data) =>
        setTopicsByChapter((prev) => ({
          ...prev,
          ...Object.fromEntries((data.chapterTopics || []).map((c) => [chapterKey(c.subjectId, c.chapterId), c.topics || []])),
        })),
      )
      .catch((err) => setError(apiErrorMessage(err, "Failed to load topics")))
      .finally(() =>
        setLoadingChapters((prev) => ({ ...prev, ...Object.fromEntries(wanted.map(([sId, cId]) => [chapterKey(sId, cId), false])) })),
      );
  };

  const toggleChapter = (sId, cId) => {
    const key = chapterKey(sId, cId);
    const open = !expanded[key];
    setExpanded((prev) => ({ ...prev, [key]: open }));
    if (open) loadChapterTopics([[sId, cId]]);
  };

  // The exam month / year changed: the loaded counts are stale, so reload every open chapter.
  const periodRef = useRef(periodKey);
  useEffect(() => {
    if (periodRef.current === periodKey) return;
    periodRef.current = periodKey;
    setTopicsByChapter({});
    if (!multiTopicMode || !courseId || !testTypeId) return;
    const openPairs = Object.keys(expanded)
      .filter((key) => expanded[key])
      .map((key) => key.split(":").map(Number));
    loadChapterTopics(openPairs, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodKey]);

  // Editing a saved test: open (and load) every chapter that already has picked items.
  const pickedChapterKeys = [...new Set(items.map((i) => chapterKey(i.subjectId, i.chapterId)))].join(",");
  useEffect(() => {
    if (!multiTopicMode || !courseId || !testTypeId || !pickedChapterKeys) return;
    const pairs = pickedChapterKeys.split(",").map((key) => key.split(":").map(Number));
    setExpanded((prev) => {
      const next = { ...prev };
      pairs.forEach(([sId, cId]) => {
        if (next[chapterKey(sId, cId)] === undefined) next[chapterKey(sId, cId)] = true;
      });
      return next;
    });
    loadChapterTopics(pairs);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [multiTopicMode, courseId, testTypeId, pickedChapterKeys]);

  const subjects = options?.subjects || [];
  const chaptersOfSubject = subjects.find((s) => s.subjectId === subjectId)?.chapters || [];

  const isPicked = (item) => items.some((i) => keyOf(i) === keyOf(item));
  const countOf = (item) => items.find((i) => keyOf(i) === keyOf(item))?.questionCount ?? "";
  const toggle = (item) =>
    onChange({
      ...value,
      items: isPicked(item)
        ? items.filter((i) => keyOf(i) !== keyOf(item))
        : [...items, { ...item, questionCount: "" }],
    });
  const setCount = (item, count) =>
    onChange({
      ...value,
      items: items.map((i) => (keyOf(i) === keyOf(item) ? { ...i, questionCount: count } : i)),
    });

  const pickSum = items.reduce((sum, i) => sum + (Number(i.questionCount) || 0), 0);
  const total = Number(totalQuestions) || 0;
  const availableOf = (item) => {
    if (!item.topicId) {
      return subjects.find((s) => s.subjectId === item.subjectId)?.chapters.find((c) => c.chapterId === item.chapterId)
        ?.available;
    }
    const topicList = multiTopicMode
      ? topicsByChapter[chapterKey(item.subjectId, item.chapterId)] || []
      : options?.topics || [];
    const topic = topicList.find((t) => t.topicId === item.topicId);
    if (!item.subTopicId) return topic?.available;
    return topic?.subTopics.find((st) => st.subTopicId === item.subTopicId)?.available;
  };
  const overCount = items.filter((i) => {
    const available = availableOf(i);
    return isValidCount(i.questionCount) && available && Number(i.questionCount) > available.total;
  }).length;
  const invalidCount = items.filter((i) => !isValidCount(i.questionCount)).length;
  const rowDisabled = disabled || loading;

  const changeSubject = (id) => {
    setLocalSubjectId(id);
    setLocalChapterId("");
    if (items.length) onChange({ ...value, items: [] });
  };
  const changeChapter = (id) => {
    setLocalChapterId(id);
    if (items.length) onChange({ ...value, items: [] });
  };

  const renderTopicRows = (sId, cId, topics) =>
    topics.map((topic) => {
      const topicItem = { subjectId: sId, chapterId: cId, topicId: topic.topicId, subTopicId: null };
      const otherTopicLocked = singleTopic && items.length > 0 && !items.some((i) => i.topicId === topic.topicId);
      const subPicked = topic.subTopics.some((st) => isPicked({ ...topicItem, subTopicId: st.subTopicId }));
      return (
        <Box key={keyOf(topicItem)} sx={{ mb: 0.5 }}>
          <PickRow
            label={topic.name}
            available={topic.available}
            picked={isPicked(topicItem)}
            count={countOf(topicItem)}
            onToggle={() => toggle(topicItem)}
            onCount={(c) => setCount(topicItem, c)}
            disabled={rowDisabled}
            toggleDisabled={subPicked || otherTopicLocked}
          />
          {topic.subTopics.map((st) => {
            const subItem = { ...topicItem, subTopicId: st.subTopicId };
            return (
              <PickRow
                key={keyOf(subItem)}
                indent={1}
                label={st.name}
                available={st.available}
                picked={isPicked(subItem)}
                count={countOf(subItem)}
                onToggle={() => toggle(subItem)}
                onCount={(c) => setCount(subItem, c)}
                disabled={rowDisabled}
                toggleDisabled={isPicked(topicItem) || otherTopicLocked}
              />
            );
          })}
        </Box>
      );
    });

  if (!courseId || !testTypeId) {
    return null;
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {error && <Alert severity="warning">{error}</Alert>}
      {loading && <LinearProgress />}

      {singleScope && (
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
          <FormControl fullWidth size="small" disabled={disabled}>
            <InputLabel id="tdb-subject-label">Subject</InputLabel>
            <Select labelId="tdb-subject-label" label="Subject" value={subjects.some((s) => s.subjectId === subjectId) ? subjectId : ""} onChange={(e) => changeSubject(e.target.value)}>
              {subjects.map((s) => (
                <MenuItem key={s.subjectId} value={s.subjectId}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small" disabled={disabled || !subjectId}>
            <InputLabel id="tdb-chapter-label">Chapter</InputLabel>
            <Select labelId="tdb-chapter-label" label="Chapter" value={chaptersOfSubject.some((c) => c.chapterId === chapterId) ? chapterId : ""} onChange={(e) => changeChapter(e.target.value)}>
              {chaptersOfSubject.map((c) => (
                <MenuItem key={c.chapterId} value={c.chapterId}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <Box>
        <Typography sx={{ fontWeight: 700, fontSize: "0.85rem", mb: 0.5 }}>
          {level === "CHAPTER" ? "Chapters" : level === "TOPIC" ? "Topics" : "Topics / Sub-Topics"}
        </Typography>
        {singleTopic && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            A practice test covers one topic: pick the whole topic, or one or more of its sub-topics.
          </Typography>
        )}

        {level === "CHAPTER" &&
          subjects.map((subject) => (
            <Box key={subject.subjectId} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "text.secondary" }}>
                {subject.name}
              </Typography>
              {subject.chapters.map((chapter) => {
                const item = { subjectId: subject.subjectId, chapterId: chapter.chapterId, topicId: null, subTopicId: null };
                return (
                  <PickRow
                    key={keyOf(item)}
                    label={chapter.name}
                    available={chapter.available}
                    picked={isPicked(item)}
                    count={countOf(item)}
                    onToggle={() => toggle(item)}
                    onCount={(c) => setCount(item, c)}
                    disabled={rowDisabled}
                  />
                );
              })}
            </Box>
          ))}

        {singleScope && (!subjectId || !chapterId) && (
          <Typography variant="body2" color="text.secondary">
            Choose a subject and a chapter to list its topics.
          </Typography>
        )}

        {singleScope && subjectId && chapterId && renderTopicRows(subjectId, chapterId, options?.topics || [])}

        {multiTopicMode &&
          subjects.map((subject) => (
            <Box key={subject.subjectId} sx={{ mb: 1.5 }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "text.secondary" }}>
                {subject.name}
              </Typography>
              {subject.chapters.map((chapter) => {
                const key = chapterKey(subject.subjectId, chapter.chapterId);
                const open = Boolean(expanded[key]);
                const pickedHere = items.filter(
                  (i) => i.subjectId === subject.subjectId && i.chapterId === chapter.chapterId,
                ).length;
                return (
                  <Box key={key}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <IconButton
                        size="small"
                        aria-label={`${open ? "Collapse" : "Expand"} ${chapter.name}`}
                        onClick={() => toggleChapter(subject.subjectId, chapter.chapterId)}
                      >
                        {open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </IconButton>
                      <Typography sx={{ fontSize: "0.85rem", fontWeight: pickedHere ? 700 : 500 }}>
                        {chapter.name}
                        {pickedHere > 0 && (
                          <Typography component="span" sx={{ ml: 1, fontSize: "0.7rem", color: "text.secondary" }}>
                            {pickedHere} picked
                          </Typography>
                        )}
                      </Typography>
                    </Box>
                    {open && loadingChapters[key] && <LinearProgress sx={{ ml: 4, my: 0.5 }} />}
                    {open && (
                      <Box sx={{ pl: 3 }}>
                        {renderTopicRows(subject.subjectId, chapter.chapterId, topicsByChapter[key] || [])}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ))}

        {minPyqPercentage > 0 && (
          <Typography variant="caption" sx={{ display: "block", mt: 1, color: "text.secondary" }}>
            At least {minPyqPercentage}% of this test
            {total > 0 ? ` (${Math.ceil((total * minPyqPercentage) / 100)} of ${total} questions)` : ""} will be drawn
            from PYQ questions when it is published.
          </Typography>
        )}

        <Typography
          variant="caption"
          sx={{ display: "block", mt: 1, fontWeight: 600, color: pickSum === total && !invalidCount && !overCount ? "#2e7d32" : "#b26a00" }}
        >
          Picked: {pickSum} / {total} questions{pickSum === total ? "" : " — must match the total"}
          {invalidCount > 0 && ` — ${invalidCount} item(s) need a count of 1 or more`}
          {overCount > 0 && ` — ${overCount} item(s) exceed the available questions`}
        </Typography>
      </Box>
    </Box>
  );
}

export default TestDistributionBuilder;
